import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import {
  FiX,
  FiCheck,
  FiTrash2,
  FiCheckCircle,
  FiAlertTriangle,
  FiAlertCircle,
  FiInfo,
  FiCpu,
  FiBell,
  FiDollarSign,
  FiShield,
  FiActivity,
  FiExternalLink,
} from 'react-icons/fi';
import type { IconType } from 'react-icons';
import type { Notification } from '../types';

interface IconResult {
  icon: IconType;
  bgColor: string;
  borderColor: string;
}

/**
 * Get icon component based on notification type and severity
 */
const getNotificationIcon = (notification: Notification): IconResult => {
  const type = (notification as Notification & { notification_type?: string }).notification_type;
  const severity = notification.severity;

  switch (type) {
    case 'project_completed':
      return { icon: FiCheckCircle, bgColor: 'bg-green-500', borderColor: 'border-green-500' };
    case 'project_update':
    case 'project_milestone':
      return { icon: FiInfo, bgColor: 'bg-blue-500', borderColor: 'border-blue-500' };
    case 'budget_alert':
      return { icon: FiAlertTriangle, bgColor: 'bg-yellow-500', borderColor: 'border-yellow-500' };
    case 'budget_warning':
    case 'budget_overspend':
      return { icon: FiAlertCircle, bgColor: 'bg-red-500', borderColor: 'border-red-500' };
    case 'agent_incident':
      return { icon: FiCpu, bgColor: 'bg-red-500', borderColor: 'border-red-500' };
    case 'agent_recovery':
      return { icon: FiCpu, bgColor: 'bg-green-500', borderColor: 'border-green-500' };
    case 'alert_triggered':
    case 'anomaly_detected':
      return { icon: FiActivity, bgColor: 'bg-orange-500', borderColor: 'border-orange-500' };
    case 'alert_resolved':
      return { icon: FiCheckCircle, bgColor: 'bg-green-500', borderColor: 'border-green-500' };
    case 'cost_alert':
    case 'cost_optimization':
      return { icon: FiDollarSign, bgColor: 'bg-yellow-500', borderColor: 'border-yellow-500' };
    case 'guardrail_violation':
      return { icon: FiShield, bgColor: 'bg-red-500', borderColor: 'border-red-500' };
    case 'system_alert':
    case 'system_maintenance':
    case 'system_announcement':
      return { icon: FiBell, bgColor: 'bg-purple-500', borderColor: 'border-purple-500' };
    default:
      // Fall back to severity-based colors
      if (severity === 'critical') {
        return { icon: FiAlertCircle, bgColor: 'bg-red-500', borderColor: 'border-red-500' };
      }
      if (severity === 'warning') {
        return { icon: FiAlertTriangle, bgColor: 'bg-yellow-500', borderColor: 'border-yellow-500' };
      }
      return { icon: FiInfo, bgColor: 'bg-blue-500', borderColor: 'border-blue-500' };
  }
};

type ExtendedNotification = Notification & {
  notification_type?: string;
  source_service?: string;
  extra_data?: Record<string, string | number | undefined>;
};

/**
 * Get progress bar color based on notification type
 */
const getProgressBarColor = (notification: ExtendedNotification): string => {
  const type = notification.notification_type;
  if (['budget_overspend', 'budget_warning'].includes(type ?? "")) return 'bg-red-500';
  if (type === 'budget_alert') return 'bg-yellow-500';
  return 'bg-blue-500';
};

/**
 * Format relative time
 */
const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

/**
 * Get source info from notification extra_data
 */
const getSourceInfo = (notification: ExtendedNotification): string | null => {
  const extraData = notification.extra_data ?? {};

  if (extraData.project_name) return `Project: ${extraData.project_name}`;
  if (extraData.agent_name) return `Agent: ${extraData.agent_name}`;
  if (extraData.alert_name) return `Alert: ${extraData.alert_name}`;
  if (extraData.account_name) return `Account: ${extraData.account_name}`;
  if (notification.source_service) return `Source: ${notification.source_service}`;
  return null;
};

/**
 * Get budget percentage from extra_data
 */
const getBudgetPercentage = (notification: ExtendedNotification): number | null => {
  const extraData = notification.extra_data ?? {};
  return typeof extraData.percentage === 'number' ? extraData.percentage : null;
};

interface NotificationItemProps {
  notification: ExtendedNotification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onClick: (notification: ExtendedNotification) => void;
}

/**
 * Single notification item component
 */
const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
  onClick,
}) => {
  const { icon: IconComponent, bgColor, borderColor } = getNotificationIcon(notification);
  const sourceInfo = getSourceInfo(notification);
  const budgetPercentage = getBudgetPercentage(notification);
  const isRead = notification.is_read;

  return (
    <div
      onClick={() => onClick(notification)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(notification); }} role="button" tabIndex={0}
      className={`p-4 border-b border-gray-200 dark:border-gray-700 cursor-pointer ${
        isRead ? 'bg-gray-50/50 dark:bg-gray-800/50' : 'bg-gray-50 dark:bg-gray-800'
      } hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center border-2 ${borderColor}`}
        >
          <IconComponent className="w-5 h-5 text-white" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4
              className={`font-semibold text-sm ${
                isRead ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'
              }`}
            >
              {notification.title}
            </h4>
            {/* Actions */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {!isRead && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkAsRead(notification.id);
                  }}
                  className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Mark as read"
                >
                  <FiCheck className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(notification.id);
                }}
                className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                title="Delete"
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          <p className={`text-sm mt-1 ${isRead ? 'text-gray-500' : 'text-gray-600 dark:text-gray-300'}`}>
            {notification.message}
          </p>

          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
            <span>{formatTimeAgo(notification.created_at)}</span>
            {sourceInfo && (
              <>
                <span>-</span>
                <span>{sourceInfo}</span>
              </>
            )}
          </div>

          {/* Budget progress bar */}
          {budgetPercentage !== null && (
            <div className="mt-2">
              <div className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                <div
                  className={`h-full ${getProgressBarColor(notification)} rounded-full transition-all`}
                  style={{ width: `${Math.min(budgetPercentage, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Main NotificationPanel component
 */
const NotificationPanel: React.FC = () => {
  const {
    notifications,
    unreadCount,
    loading,
    isPanelOpen,
    filter,
    closePanel,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    changeFilter,
    fetchNotifications,
  } = useNotifications();

  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  const handleNotificationClick = (notification: ExtendedNotification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    closePanel();
    navigate('/hub/notifications');
  };

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        // Check if click is on the notification bell button
        const bellButton = document.getElementById('notification-bell-button');
        if (bellButton?.contains(event.target as Node)) {
          return;
        }
        closePanel();
      }
    };

    if (isPanelOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isPanelOpen, closePanel]);

  // Refresh notifications when panel opens
  useEffect(() => {
    if (isPanelOpen) {
      fetchNotifications();
    }
  }, [isPanelOpen, fetchNotifications]);

  if (!isPanelOpen) {
    return null;
  }

  let notificationListContent: React.ReactElement;
  if (loading) {
    notificationListContent = (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  } else if (notifications.length === 0) {
    notificationListContent = (
      <div className="flex flex-col items-center justify-center h-32 text-gray-500">
        <FiBell className="w-8 h-8 mb-2" />
        <p className="text-sm">
          {filter === 'unread' ? 'No unread notifications' : 'No notifications'}
        </p>
      </div>
    );
  } else {
    notificationListContent = (
      <>
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification as ExtendedNotification}
            onMarkAsRead={markAsRead}
            onDelete={deleteNotification}
            onClick={handleNotificationClick}
          />
        ))}
      </>
    );
  }

  return (
    <div
      ref={panelRef}
      className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notifications</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{unreadCount} unread</p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Mark all as read"
            >
              <FiCheck className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={closePanel}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title="Close"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 p-4 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => changeFilter('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          All
        </button>
        <button
          onClick={() => changeFilter('unread')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            filter === 'unread'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          Unread
        </button>
      </div>

      {/* Notification list */}
      <div className="flex-1 overflow-y-auto">
        {notificationListContent}
      </div>

      {/* Footer with View All link */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4">
        <Link
          to="/hub/notifications"
          onClick={closePanel}
          className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300 dark:hover:text-white rounded-lg transition-colors text-sm font-medium"
        >
          <FiExternalLink className="w-4 h-4" />
          View All Notifications
        </Link>
      </div>
    </div>
  );
};

export default NotificationPanel;
